import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getContent, saveContent } from "../lib/api";
import { applyTheme } from "../lib/theme";
import { SEO_DEFAULTS } from "../admin/seoSchema";
import { slugify, uniqueSlug } from "../lib/slug";

const ContentContext = createContext(null);

export const useContent = () => useContext(ContentContext);

// Recursively fill any missing keys in `target` from `defaults`. Non-object
// values in `target` are kept as-is (never overwritten). We only ADD keys.
const deepFillDefaults = (target, defaults) => {
  const out = { ...(target || {}) };
  let changed = false;
  for (const [k, v] of Object.entries(defaults)) {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const nested = deepFillDefaults(out[k], v);
      if (nested.changed) changed = true;
      out[k] = nested.value;
    } else if (out[k] === undefined || out[k] === null || out[k] === "") {
      out[k] = v;
      changed = true;
    }
  }
  return { value: out, changed };
};

// Ensure every treatment area has a stable `id` and `slug`. Missing values
// are filled from the tile name so the admin edit-page link + public route
// resolve without requiring a manual save first. Returns `changed` if the
// caller should persist the merged view back to Supabase.
const ensureTreatmentAreaKeys = (areas) => {
  if (!Array.isArray(areas)) return { value: areas, changed: false };
  let changed = false;
  const taken = new Set(areas.map((a) => a?.slug).filter(Boolean));
  const value = areas.map((a) => {
    if (!a || typeof a !== "object") return a;
    const patch = { ...a };
    if (!patch.id) { patch.id = `ta-${Math.random().toString(36).slice(2, 10)}`; changed = true; }
    if (!patch.slug && patch.name) {
      patch.slug = uniqueSlug(slugify(patch.name), Array.from(taken));
      taken.add(patch.slug);
      changed = true;
    }
    return patch;
  });
  return { value, changed };
};

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await getContent();
      // One-time seeds: (1) SEO defaults, (2) treatment-area IDs+slugs.
      const seededSeo = deepFillDefaults(data?.seo, SEO_DEFAULTS);
      const seededAreas = ensureTreatmentAreaKeys(data?.treatment?.areas);
      const anyChanged = seededSeo.changed || seededAreas.changed;
      if (anyChanged) {
        const withDefaults = {
          ...data,
          seo: seededSeo.value,
          treatment: { ...(data?.treatment || {}), areas: seededAreas.value },
        };
        try {
          const saved = await saveContent(withDefaults);
          setContent(saved);
          return;
        } catch {
          setContent(withDefaults);
          return;
        }
      }
      setContent(data);
    } catch (e) {
      setContent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Apply the saved brand palette whenever content loads or is saved.
  useEffect(() => {
    if (content?.theme) applyTheme(content.theme);
  }, [content]);

  if (loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="h-10 w-10 rounded-full border-2 border-sage/30 border-t-sage animate-spin" />
      </div>
    );
  }

  return (
    <ContentContext.Provider value={{ content, refresh, setContent }}>
      {children}
    </ContentContext.Provider>
  );
};
